import { Center, Stack, Table, Text } from '@mantine/core'
import { useModals } from '@mantine/modals'
import LinkButton from 'common/LinkButton'
import Loader from 'common/Loader'
import dayjs from 'dayjs'
import { getErrorMessage } from 'errors'
import { useOrderHistory } from 'hooks/orders'
import { useEffect, useState } from 'react'
import { OrderWithHistory } from 'types/orders'
import OrderHistorySelectTable from './OrderHistorySelectTable'
import OrderHistoryTable from './OrderHistoryTable'

type Props = {
  search: string
}

export default function OrderHistory({ search }: Props) {
  const { data, isLoading, isFetching, isError, error, isSuccess } = useOrderHistory(search)

  const [orderHistory, setOrderHistory] = useState<OrderWithHistory | null>(null)

  useEffect(() => {
    if (isSuccess && !isFetching) {
      if (data.length === 1) {
        setOrderHistory(data[0])
      } else if (data.length === 0) {
        setOrderHistory(null)
      }
    }
  }, [data, isSuccess, isFetching])

  if (isLoading || isFetching) {
    return <Loader />
  } else if (isError) {
    return (
      <Center>
        <Text c="red">Wystąpił błąd podczas ładowania historii zamówień: {getErrorMessage(error)}</Text>
      </Center>
    )
  } else if (isSuccess) {
    if (data.length === 0) {
      return (
        <Center>
          <Text c="dimmed">Brak historii zamówień dla podanego wyszukiwania.</Text>
        </Center>
      )
    } else if (orderHistory) {
      return (
        <Stack justify="start" align="start">
          <OrderPreviewTable order={orderHistory.order} />
          <OrderHistoryTable data={orderHistory} />
        </Stack>
      )
    } else {
      return (
        <Stack mt="xl">
          <Text size="sm">
            Znaleziono {data.length} {getZamowienia(data.length)} do wyszukiwania <b>"{search}"</b>. Wybierz zamówienie,
            aby zobaczyć jego historię:
          </Text>
          <OrderHistorySelectTable data={data} onRowClick={(order) => setOrderHistory(order)} />
        </Stack>
      )
    }
  }
}

function getZamowienia(count: number) {
  return count === 1 ? 'zamówienie pasujące' : count >= 2 && count <= 4 ? 'zamówienia pasujące' : 'zamówień pasujących'
}

function OrderPreviewTable({ order }: { order: OrderWithHistory['order'] }) {
  const modals = useModals()

  return (
    <Table variant="vertical" withTableBorder>
      <Table.Tbody>
        <Table.Tr>
          <Table.Th w={160}>Numer zamówienia</Table.Th>
          <Table.Td>
            {order.id.startsWith('Z-') ? (
              <LinkButton
                to={`/orders/details/${order.id.slice(2)}`}
                onClick={() => {
                  modals.closeAll()
                }}
              >
                {order.id}
              </LinkButton>
            ) : (
              order.id
            )}
          </Table.Td>
        </Table.Tr>
        {order.createdAt && (
          <Table.Tr>
            <Table.Th>Data utworzenia</Table.Th>
            <Table.Td>{dayjs(order.createdAt).format('DD.MM.YYYY HH:mm')}</Table.Td>
          </Table.Tr>
        )}
        {order.firstName && (
          <Table.Tr>
            <Table.Th>Imię</Table.Th>
            <Table.Td>{order.firstName}</Table.Td>
          </Table.Tr>
        )}
        {order.lastName && (
          <Table.Tr>
            <Table.Th>Nazwisko</Table.Th>
            <Table.Td>{order.lastName}</Table.Td>
          </Table.Tr>
        )}
        {order.email && (
          <Table.Tr>
            <Table.Th>Email</Table.Th>
            <Table.Td>{order.email}</Table.Td>
          </Table.Tr>
        )}
        {order.phoneNumber && (
          <Table.Tr>
            <Table.Th>Numer telefonu</Table.Th>
            <Table.Td>
              {order.countryCode ? `+${order.countryCode} ` : ''}
              {order.phoneNumber}
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  )
}
